import {
  createMemoryHistory,
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteMeta,
} from 'vue-router';

import { useSessionStore } from './stores/session';

const loadLoginPage = () => import('./pages/LoginPage.vue');
const loadAdminLayout = () => import('./layouts/AdminLayout.vue');
const loadClassesPage = () => import('./pages/ClassesPage.vue');
const loadStudentOverviewPage = () => import('./pages/StudentOverviewPage.vue');
const loadClassDetailPage = () => import('./pages/ClassDetailPage.vue');
const loadClassStudentsPage = () => import('./pages/ClassStudentsPage.vue');
const loadRulesPage = () => import('./pages/RulesPage.vue');
const loadRankingPage = () => import('./pages/RankingPage.vue');
const loadPetTemplatesPage = () => import('./pages/PetTemplatesPage.vue');
const loadFoodManagementPage = () => import('./pages/FoodManagementPage.vue');
const loadSkinManagementPage = () => import('./pages/SkinManagementPage.vue');
const loadPlatformAdminsPage = () => import('./pages/PlatformAdminsPage.vue');
const loadOrgsPage = () => import('./pages/OrgsPage.vue');
const loadTeachersPage = () => import('./pages/TeachersPage.vue');
const loadShopManagementPage = () => import('./pages/ShopManagementPage.vue');
const loadCookbookPage = () => import('./pages/CookbookPage.vue');

export const router = createRouter({
  history:
    typeof window === 'undefined'
      ? createMemoryHistory(import.meta.env.BASE_URL)
      : createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: loadLoginPage,
      meta: {
        public: true,
        title: '登录',
      },
    },
    {
      path: '/cookbook',
      name: 'cookbook',
      component: loadCookbookPage,
      meta: {
        public: true,
        title: '操作手册',
      },
    },
    {
      path: '/cookbook/:role',
      name: 'cookbook-role',
      component: loadCookbookPage,
      meta: {
        public: true,
        title: '操作手册',
      },
    },
    {
      path: '/',
      component: loadAdminLayout,
      children: [
        {
          path: '',
          redirect: '/classes',
        },
        {
          path: '/classes',
          name: 'classes',
          component: loadClassesPage,
          meta: {
            title: '班级运营仪表盘',
            subtitle: '按学期与老师筛选，并设为当前操作班级',
          },
        },
        {
          path: '/students',
          name: 'student-overview',
          component: loadStudentOverviewPage,
          meta: {
            title: '学员总览',
            subtitle: '跨班级查看学员成长、宠物与资产信息',
            allowedRoles: ['ORG_ADMIN', 'ADMIN', 'TEACHER'],
          },
        },
        {
          path: '/classes/:classId',
          name: 'class-detail',
          component: loadClassDetailPage,
          meta: {
            title: '班级配置工作区',
            compactChrome: true,
          },
        },
        {
          path: '/classes/:classId/students',
          name: 'class-students',
          component: loadClassStudentsPage,
          meta: {
            title: '班级操作中台',
            compactChrome: true,
          },
        },
        {
          path: '/rules',
          name: 'rules',
          component: loadRulesPage,
          meta: {
            title: '奖励规则决策页',
            subtitle: '机构通用与班级私有规则，含食物配置',
            allowedRoles: ['ORG_ADMIN', 'ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN'],
          },
        },
        {
          path: '/ranking',
          name: 'ranking',
          component: loadRankingPage,
          meta: {
            title: '班级成长排行',
            subtitle: '按班级查看成长积分排名',
            theme: 'violet',
          },
        },
        {
          path: '/pet-foods',
          name: 'pet-foods',
          component: loadFoodManagementPage,
          meta: {
            title: '食物管理',
            subtitle: '机构食物配置，全局默认只读',
            allowedRoles: ['ORG_ADMIN', 'ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN'],
          },
        },
        {
          path: '/pet-skins',
          name: 'pet-skins',
          component: loadSkinManagementPage,
          meta: {
            title: '兑换管理',
            subtitle: '宠物皮肤全局字典与默认定价',
            allowedRoles: ['PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN'],
          },
        },
        {
          path: '/pet-templates',
          name: 'pet-templates',
          component: loadPetTemplatesPage,
          meta: {
            title: '宠物形象管理',
            subtitle: '机构宠物形象模板管理',
            allowedRoles: ['ORG_ADMIN', 'ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN'],
          },
        },
        {
          path: '/platform-admins',
          name: 'platform-admins',
          component: loadPlatformAdminsPage,
          meta: {
            title: '平台管理员',
          },
        },
        {
          path: '/orgs',
          name: 'orgs',
          component: loadOrgsPage,
          meta: {
            title: '机构管理',
            allowedRoles: ['PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN'],
          },
        },
        {
          path: '/teachers',
          name: 'teachers',
          component: loadTeachersPage,
          meta: {
            title: '老师管理',
            subtitle: '机构管理员创建老师账号并绑定班级',
          },
        },
        {
          path: '/shop',
          name: 'shop-management',
          component: loadShopManagementPage,
          meta: {
            title: '兑换管理',
            subtitle: '皮肤上下架与发放规则',
            allowedRoles: ['ORG_ADMIN', 'ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN'],
          },
        },
      ],
    },
  ],
});

router.beforeEach((to: RouteLocationNormalized) => {
  const sessionStore = useSessionStore();
  sessionStore.hydrate();

  if (to.meta.public) {
    if (sessionStore.token && to.name === 'login') {
      return { path: getDefaultRouteForRole(sessionStore.user?.roleType) };
    }
    return true;
  }

  if (!sessionStore.token) {
    return {
      name: 'login',
      query: {
        redirect: to.fullPath,
      },
    };
  }

  if (!canAccessRouteForRole(to, sessionStore.user?.roleType)) {
    return { path: getDefaultRouteForRole(sessionStore.user?.roleType), replace: true };
  }

  return true;
});

export function canAccessRouteForRole(route: { meta: RouteMeta; path?: unknown }, roleType?: string) {
  const routePath = typeof route.path === 'string' ? route.path : '';
  const allowedRoles =
    routePath.startsWith('/rules') || routePath.startsWith('/pet-foods')
      ? ['ORG_ADMIN', 'ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN']
      : (route.meta.allowedRoles as string[] | undefined);
  if (!allowedRoles) {
    return true;
  }

  return roleType !== undefined && allowedRoles.includes(roleType);
}

function getDefaultRouteForRole(roleType?: string) {
  if (roleType === 'PLATFORM_SUPER_ADMIN') {
    return '/platform-admins';
  }
  if (roleType === 'PLATFORM_ADMIN') {
    return '/orgs';
  }
  return '/classes';
}
